import { createRouter } from 'vue-router';
import { displayModeValues } from '@/Plugins/DisplayModes';
import { createHashRouterHistory } from '@/Utils/HashRouterHistory';
import {
    getHashPath,
    getHistoryState,
    getPathnameWithSearch,
    getScrollY,
    onWindow,
    pushState,
    redirectTo,
    replaceState,
    scrollToY,
} from '@/Utils/BrowserNavigation';

// Catch-all fallback route — Router is installed via app.use(Router) for every
// Vue component on a page (Core.js), but most pages don't call App.Router.addRoute().
// Without a matching route, vue-router emits "No match found for location with path".
const routes = [{
    path: '/:pathMatch(.*)*',
    meta: { noScroll: true },
    component: { render: () => null },
}];

const savedScrollPositionsLimit = 50;

function scrollBehavior(to, from, savedPosition) {
    if (to.matched.some((record) => record.meta.noScroll) || to.query.focusFields || from?.meta?.isModalVisible) {
        return null;
    }
    else if (savedPosition) {
        return savedPosition;
    }
    else {
        return { left: 0, top: 0 };
    }
}

// Old #/admin links still circulate in emails and elsewhere, so on the backer-facing site
// they have to land on /admin - a separate app with its own router.
function redirectLegacyAdminHash(hashPath) {
    if (!hashPath?.startsWith('/admin')) {
        return false;
    }

    redirectTo(hashPath);
    return true;
}

export function createAppRouter() {
    const savedScrollPositions = new Map();
    const teardowns = [];

    const initialHashPath = getHashPath();

    // The redirect is a cross-document navigation, so this page keeps running while it happens.
    // Nothing here may touch the url it is leaving from, or a reload cannot retry the legacy link.
    let isLeaving = redirectLegacyAdminHash(initialHashPath);

    const history = createHashRouterHistory();

    const router = createRouter({
        history,
        routes,
        scrollBehavior,
    });

    router.previousRoute = null;

    function saveScrollPosition(from) {
        if (from?.meta?.noScroll === true || !from?.name) {
            return;
        }

        savedScrollPositions.delete(from.fullPath);
        savedScrollPositions.set(from.fullPath, { y: getScrollY() });

        if (savedScrollPositions.size > savedScrollPositionsLimit) {
            savedScrollPositions.delete(savedScrollPositions.keys().next().value);
        }
    }

    router.beforeEach((to, from) => {
        const savedScrollPosition = savedScrollPositions.get(to.fullPath);

        // Saved before the restore below, which is what afterEach used to read back instead.
        saveScrollPosition(from);

        if (savedScrollPosition && from?.meta && !from.meta.noScroll) {
            scrollToY(savedScrollPosition.y);
        }
        router.previousRoute = from;
    });

    router.afterEach((to, from) => {
        if (isLeaving) {
            return;
        }

        const { isWeb } = displayModeValues;

        if (!to || !isWeb) {
            return;
        }

        const toFullPath = to.fullPath;

        if (getHashPath() === toFullPath) {
            return;
        }

        const pathnameWithSearch = getPathnameWithSearch();

        // A route with no name is the catch-all, so the page has nothing worth addressing.
        const url = to.name ? `${pathnameWithSearch}#${toFullPath}` : pathnameWithSearch;
        const write = history.wasReplace() ? replaceState : pushState;

        write({ from: from?.fullPath, to: toFullPath }, url);
    });

    function handleHashChange() {
        const { isWeb, isPWA } = displayModeValues;
        const hashPath = getHashPath();

        // A fragment-only change never reloads the page, so the redirect has to run here too.
        if (redirectLegacyAdminHash(hashPath)) {
            isLeaving = true;
            return;
        }

        const routerPath = router.currentRoute.value.fullPath;
        const isHashPathEmpty = hashPath !== routerPath && getHistoryState() === null;

        if (isHashPathEmpty && isWeb) {
            router.push(hashPath ? hashPath : '/').catch(() => {});
        }
        else if (isHashPathEmpty && isPWA) {
            router.replace(hashPath ? hashPath : '/').catch(() => {});
        }
    }

    function handlePopstate() {
        const { isWeb } = displayModeValues;
        const hashPath = getHashPath();
        const routerPath = router.currentRoute.value.fullPath;

        if (hashPath !== routerPath && getHistoryState() !== null && isWeb) {
            router.replace(hashPath ? hashPath : '/').catch(() => {});
        }
    }

    if (initialHashPath) {
        router.push(initialHashPath).catch(() => {});
    }

    teardowns.push(onWindow('hashchange', handleHashChange));
    teardowns.push(onWindow('popstate', handlePopstate));

    router.dispose = () => {
        teardowns.splice(0).forEach((teardown) => teardown());
    };

    return router;
}

export default createAppRouter();
